import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import API, { resolveAssetUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Search, UserPlus, Check, X, Send, ChevronLeft,
  Users, MessageSquare, UserCheck,
} from 'lucide-react';
import { io } from 'socket.io-client';
import Toast from '../components/Toast';

const SOCKET_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

// ── Avatar ─────────────────────────────────────────────────────────────────────
const AVATAR_COLORS = ['#f59e0b','#10b981','#3b82f6','#8b5cf6','#ef4444','#ec4899'];

function Avatar({ user, size = 36, online = false }) {
  const name   = user?.display_name || user?.username || '?';
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const color  = AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
  return (
    <div style={{ position: 'relative', flexShrink: 0, width: size, height: size }}>
      {user?.avatar_url && user.avatar_url !== 'none' ? (
        <img
          src={resolveAssetUrl(user.avatar_url)} alt={name}
          style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      ) : (
        <div style={{
          width: size, height: size, borderRadius: '50%',
          background: color + '22', color, border: `1.5px solid ${color}55`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: size > 40 ? '1rem' : size > 28 ? '0.75rem' : '0.6rem',
          fontFamily: 'var(--font-display)',
        }}>
          {initials}
        </div>
      )}
      {online && (
        <span style={{
          position: 'absolute', bottom: 1, right: 1,
          width: Math.max(8, size * 0.22), height: Math.max(8, size * 0.22),
          borderRadius: '50%', background: '#22c55e',
          border: '2px solid var(--bg-card)',
        }} />
      )}
    </div>
  );
}

// ── Date / time helpers ────────────────────────────────────────────────────────
function dateSep(ts) {
  const d   = new Date(ts);
  const now = new Date();
  const diffDays = Math.floor((now.setHours(0,0,0,0) - new Date(d).setHours(0,0,0,0)) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short',
    year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}

function fmtTime(ts) {
  return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function fmtListTime(ts) {
  const d   = new Date(ts);
  const now = new Date();
  const diffDays = Math.floor((now.setHours(0,0,0,0) - new Date(d).setHours(0,0,0,0)) / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// ── Message grouping ──────────────────────────────────────────────────────────
function buildGroups(messages) {
  const out = [];
  let lastDate   = null;
  let lastSender = null;
  let lastMs     = null;

  for (let i = 0; i < messages.length; i++) {
    const msg    = messages[i];
    const dateStr = new Date(msg.created_at).toDateString();
    const ms     = new Date(msg.created_at).getTime();

    if (dateStr !== lastDate) {
      out.push({ type: 'sep', label: dateSep(msg.created_at), id: `sep-${msg.id}` });
      lastDate   = dateStr;
      lastSender = null;
      lastMs     = null;
    }

    const sameGroup = lastSender === msg.sender_id && lastMs && (ms - lastMs) < 3 * 60 * 1000;
    if (sameGroup && out.length) {
      const prev = out[out.length - 1];
      if (prev.type === 'msg') prev.isLast = false;
    }
    out.push({ type: 'msg', msg, isFirst: !sameGroup, isLast: true, id: msg.id });
    lastSender = msg.sender_id;
    lastMs     = ms;
  }
  return out;
}

// ── TypingDots ────────────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '6px 10px' }}>
      {[0,1,2].map(i => (
        <span key={i} style={{
          width: 6, height: 6, borderRadius: '50%', background: 'var(--text-muted)',
          display: 'inline-block',
          animation: `typing-dot 1.2s ease infinite`,
          animationDelay: `${i * 0.2}s`,
        }} />
      ))}
    </div>
  );
}

// ── ChatWindow ─────────────────────────────────────────────────────────────────
function ChatWindow({ friend, currentUser, onBack, isMobile }) {
  const [messages,  setMessages]  = useState([]);
  const [input,     setInput]     = useState('');
  const [sending,   setSending]   = useState(false);
  const [friendTyping, setFriendTyping] = useState(false);
  const bottomRef   = useRef(null);
  const inputRef    = useRef(null);
  const socketRef   = useRef(null);
  const typingTimer = useRef(null);
  const groups      = useMemo(() => buildGroups(messages), [messages]);

  // ── socket.io ──
  useEffect(() => {
    const token = localStorage.getItem('ipl_token');
    if (!token) return;
    const socket = io(SOCKET_URL, { auth: { token }, transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('user_typing',      ({ from }) => { if (from === friend.friend_id) setFriendTyping(true);  });
    socket.on('user_stop_typing', ({ from }) => { if (from === friend.friend_id) setFriendTyping(false); });
    socket.on('connect_error', () => {});

    return () => { socket.disconnect(); socketRef.current = null; };
  }, [friend.friend_id]);

  // ── fetch messages + mark as read ──
  const fetchMessages = useCallback(async () => {
    try {
      const r = await API.get(`/friends/chat/${friend.friend_id}`);
      setMessages(r.data);
    } catch {}
  }, [friend.friend_id]);

  // Mark messages as read whenever this chat is opened or messages arrive
  const markRead = useCallback(async () => {
    try {
      await API.put(`/friends/chat/${friend.friend_id}/read`);
    } catch {}
  }, [friend.friend_id]);

  useEffect(() => {
    fetchMessages().then(() => markRead());
    const iv = setInterval(() => {
      fetchMessages();
      markRead();
    }, 4000);
    return () => clearInterval(iv);
  }, [fetchMessages, markRead]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, friendTyping]);

  // ── typing emit ──
  const emitTyping = useCallback((isTyping) => {
    socketRef.current?.emit(isTyping ? 'typing' : 'stop_typing', { to: friend.friend_id });
  }, [friend.friend_id]);

  const handleInputChange = useCallback(e => {
    setInput(e.target.value);
    // Auto-resize textarea
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 96) + 'px';
    // Typing events
    emitTyping(true);
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => emitTyping(false), 2000);
  }, [emitTyping]);

  const handleKeyDown = useCallback(e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !sending) sendMessage();
    }
  }, [input, sending]); // eslint-disable-line

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    emitTyping(false);
    clearTimeout(typingTimer.current);
    setInput('');
    if (inputRef.current) { inputRef.current.style.height = 'auto'; }
    try {
      await API.post(`/friends/chat/${friend.friend_id}`, { content: text });
      await fetchMessages();
    } catch {}
    finally { setSending(false); }
  }, [input, sending, friend.friend_id, fetchMessages, emitTyping]);

  const me = currentUser?.id;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', background: 'var(--bg-primary)' }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 16px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg-card)', flexShrink: 0,
      }}>
        {isMobile && (
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px 6px 4px 0', display: 'flex' }}>
            <ChevronLeft size={20} />
          </button>
        )}
        <Avatar user={{ username: friend.username, display_name: friend.display_name, avatar_url: friend.avatar_url }} size={38} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
            {friend.display_name || friend.username}
          </div>
          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>@{friend.username}</div>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px', display: 'flex', flexDirection: 'column' }}>

        {messages.length === 0 && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: 'var(--text-muted)', padding: '2rem' }}>
            <MessageSquare size={40} style={{ opacity: 0.2 }} />
            <div style={{ fontSize: '0.85rem' }}>No messages yet</div>
            <div style={{ fontSize: '0.75rem' }}>Say hello to {friend.display_name || friend.username}! 👋</div>
          </div>
        )}

        {groups.map(item => {
          if (item.type === 'sep') return (
            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0 8px', userSelect: 'none' }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span style={{ fontSize: '0.67rem', color: 'var(--text-muted)', fontWeight: 500, whiteSpace: 'nowrap' }}>{item.label}</span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
          );

          const { msg, isFirst, isLast } = item;
          const isMe = msg.sender_id === me;
          // Unread: received message that hasn't been read yet
          const isUnread = !isMe && msg.read === false;

          return (
            <div key={item.id} style={{
              display: 'flex', flexDirection: 'column',
              alignItems: isMe ? 'flex-end' : 'flex-start',
              marginBottom: isLast ? 10 : 2,
              paddingLeft: isMe ? 40 : 0,
              paddingRight: isMe ? 0 : 40,
              background: isUnread ? 'rgba(245,158,11,0.04)' : 'transparent',
              borderRadius: 6,
            }}>
              {/* Avatar row for received messages */}
              {!isMe && isFirst && (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 7, marginBottom: 2 }}>
                  <Avatar user={{ username: friend.username, display_name: friend.display_name, avatar_url: friend.avatar_url }} size={22} />
                  <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                    {friend.display_name || friend.username}
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, width: '100%' }}>
                {/* Spacer for avatar alignment on received messages */}
                {!isMe && !isFirst && <div style={{ width: 22, flexShrink: 0 }} />}

                <div style={{
                  flex: 1, minWidth: 0,
                  padding: '9px 13px',
                  borderRadius: isMe
                    ? `16px 16px ${isLast ? '4px' : '16px'} 16px`
                    : `16px 16px 16px ${isLast ? '4px' : '16px'}`,
                  background: isMe ? 'var(--accent)' : isUnread ? 'rgba(245,158,11,0.15)' : 'var(--bg-elevated)',
                  border: isUnread ? '1px solid rgba(245,158,11,0.3)' : '1px solid transparent',
                  color: isMe ? '#000' : 'var(--text-primary)',
                  fontSize: '0.875rem',
                  lineHeight: 1.55,
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                }}>
                  {msg.content}
                  {isLast && (
                    <div style={{
                      fontSize: '0.6rem', marginTop: 4, textAlign: isMe ? 'right' : 'left',
                      opacity: 0.6, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                    }}>
                      {fmtTime(msg.created_at)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Typing indicator */}
        {friendTyping && (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 7, marginBottom: 8 }}>
            <Avatar user={{ username: friend.username, display_name: friend.display_name, avatar_url: friend.avatar_url }} size={22} />
            <div style={{
              padding: '4px 12px', borderRadius: '16px 16px 16px 4px',
              background: 'var(--bg-elevated)', display: 'inline-flex',
            }}>
              <TypingDots />
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div style={{
        padding: '10px 14px', borderTop: '1px solid var(--border)',
        background: 'var(--bg-card)', flexShrink: 0,
      }}>
        <div style={{
          display: 'flex', alignItems: 'flex-end', gap: 8,
          background: 'var(--bg-secondary)', border: '1px solid var(--border)',
          borderRadius: 22, padding: '6px 6px 6px 14px',
          transition: 'border-color 0.15s',
        }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Message… (Enter to send, Shift+Enter for new line)"
            maxLength={500}
            rows={1}
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none', resize: 'none',
              color: 'var(--text-primary)', fontSize: '0.875rem', lineHeight: 1.5,
              padding: '4px 0', overflowY: 'hidden', fontFamily: 'inherit',
              maxHeight: 96,
            }}
          />
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, paddingBottom: 2 }}>
            {input.length > 400 && (
              <span style={{ fontSize: '0.62rem', color: input.length > 480 ? 'var(--red)' : 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                {500 - input.length}
              </span>
            )}
            <button
              onClick={sendMessage}
              disabled={!input.trim() || sending}
              style={{
                width: 34, height: 34, borderRadius: '50%', border: 'none', flexShrink: 0,
                background: input.trim() ? 'var(--accent)' : 'rgba(255,255,255,0.06)',
                color: input.trim() ? '#000' : 'var(--text-muted)',
                cursor: input.trim() ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s, transform 0.1s',
                transform: input.trim() ? 'scale(1)' : 'scale(0.9)',
              }}
            >
              <Send size={14} strokeWidth={2.5} />
            </button>
          </div>
        </div>
        <div style={{ textAlign: 'center', fontSize: '0.6rem', color: 'var(--text-muted)', marginTop: 5, opacity: 0.5 }}>
          Text only · No files or media
        </div>
      </div>
    </div>
  );
}

// ── Friend row in sidebar ──────────────────────────────────────────────────────
function FriendRow({ f, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 11,
        padding: '11px 14px', border: 'none', cursor: 'pointer', textAlign: 'left',
        background: isActive ? 'rgba(245,158,11,0.08)' : 'transparent',
        borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent',
        borderRadius: 0, transition: 'background 0.12s',
      }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg-elevated)'; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
    >
      <Avatar user={{ username: f.username, display_name: f.display_name, avatar_url: f.avatar_url }} size={42} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
          <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {f.display_name || f.username}
          </span>
          {f.last_message_at && (
            <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', flexShrink: 0, marginLeft: 6 }}>
              {fmtListTime(f.last_message_at)}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
          <span style={{
            fontSize: '0.75rem', color: f.unread_count > 0 ? 'var(--text-secondary)' : 'var(--text-muted)',
            fontWeight: f.unread_count > 0 ? 500 : 400,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {f.last_message ? (f.last_message.length > 36 ? f.last_message.slice(0, 36) + '…' : f.last_message) : <span style={{ opacity: 0.5 }}>No messages yet</span>}
          </span>
          {f.unread_count > 0 && (
            <span style={{
              background: 'var(--accent)', color: '#000', borderRadius: 10,
              padding: '1px 6px', fontSize: '0.65rem', fontWeight: 700, flexShrink: 0,
            }}>
              {f.unread_count}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ── Request row ───────────────────────────────────────────────────────────────
function RequestRow({ r, onAccept, onDecline }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 14px', borderBottom: '1px solid var(--border)' }}>
      <Avatar user={{ username: r.username, display_name: r.display_name, avatar_url: r.avatar_url }} size={42} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
          {r.display_name || r.username}
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>@{r.username} wants to be friends</div>
      </div>
      <button onClick={onAccept} title="Accept"
        style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'rgba(34,197,94,0.15)', color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Check size={15} strokeWidth={2.5} />
      </button>
      <button onClick={onDecline} title="Decline"
        style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'rgba(239,68,68,0.12)', color: 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <X size={15} strokeWidth={2.5} />
      </button>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function FriendsPage() {
  const { user }                  = useAuth();
  const [friends,  setFriends]    = useState([]);
  const [requests, setRequests]   = useState([]);
  const [search,   setSearch]     = useState('');
  const [results,  setResults]    = useState([]);
  const [tab,      setTab]        = useState('friends');
  const [chatWith, setChatWith]   = useState(null);
  const [toast,    setToast]      = useState(null);
  const [loading,  setLoading]    = useState(true);
  const [isMobile, setIsMobile]   = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [fRes, rRes] = await Promise.all([API.get('/friends'), API.get('/friends/requests')]);
      setFriends(fRes.data);
      setRequests(rRes.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Debounced search
  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await API.get(`/friends/search?q=${encodeURIComponent(search)}`); setResults(r.data); }
      catch {}
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const sendRequest = async (userId) => {
    try {
      await API.post(`/friends/request/${userId}`);
      setToast({ msg: 'Friend request sent!', type: 'success' });
      setSearch(''); setResults([]);
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Failed', type: 'error' });
    }
  };

  const acceptRequest = async (userId) => {
    try { await API.post(`/friends/accept/${userId}`); setToast({ msg: 'Friend added!', type: 'success' }); fetchAll(); }
    catch { setToast({ msg: 'Failed', type: 'error' }); }
  };

  const declineRequest = async (userId) => {
    try { await API.post(`/friends/decline/${userId}`); fetchAll(); } catch {}
  };

  // ── Layout ──
  // desktop: 100vh minus navbar(~60px) + page-header(~64px) + main padding(40px)
  const panelHeight = `calc(100vh - ${isMobile ? '130px' : '170px'})`;

  const sidebar = (
    <div style={{
      width: isMobile ? '100%' : 300, flexShrink: 0,
      display: 'flex', flexDirection: 'column',
      borderRight: isMobile ? 'none' : '1px solid var(--border)',
      height: panelHeight,
    }}>
      {/* Search */}
      <div style={{ padding: '14px 12px 10px', borderBottom: '1px solid var(--border)', position: 'relative' }}>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search players…"
            style={{
              width: '100%', paddingLeft: 32, paddingRight: 12, paddingTop: 8, paddingBottom: 8,
              background: 'var(--bg-secondary)', border: '1px solid var(--border)',
              borderRadius: 20, color: 'var(--text-primary)', fontSize: '0.82rem', outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        {results.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 12, right: 12,
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderRadius: 10, zIndex: 50, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          }}>
            {results.map(u => (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 12px', borderBottom: '1px solid var(--border)' }}>
                <Avatar user={u} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{u.display_name || u.username}</div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>@{u.username}</div>
                </div>
                <button onClick={() => sendRequest(u.id)} style={{ padding: '4px 10px', borderRadius: 12, border: '1px solid var(--accent)', background: 'transparent', color: 'var(--accent)', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <UserPlus size={11} /> Add
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        {[['friends', 'Friends', friends.length], ['requests', 'Requests', requests.length]].map(([key, label, count]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            flex: 1, padding: '10px 0', border: 'none', cursor: 'pointer', background: 'none',
            color: tab === key ? 'var(--accent)' : 'var(--text-muted)',
            borderBottom: tab === key ? '2px solid var(--accent)' : '2px solid transparent',
            fontSize: '0.8rem', fontWeight: 600, transition: 'color 0.15s',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            {label}
            {count > 0 && (
              <span style={{
                background: key === 'requests' ? 'var(--red)' : 'var(--bg-elevated)',
                color: key === 'requests' ? '#fff' : 'var(--text-muted)',
                borderRadius: 10, padding: '0px 6px', fontSize: '0.65rem', fontWeight: 700,
              }}>{count}</span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading && <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>Loading…</div>}

        {tab === 'friends' && !loading && (
          friends.length === 0
            ? <div style={{ padding: '3rem 1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                <Users size={36} style={{ opacity: 0.2, marginBottom: 10, display: 'block', margin: '0 auto 10px' }} />
                <div style={{ fontSize: '0.82rem' }}>No friends yet</div>
                <div style={{ fontSize: '0.72rem', marginTop: 4 }}>Search above to add players</div>
              </div>
            : friends.map(f => (
                <div key={f.friendship_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <FriendRow f={f} isActive={chatWith?.friendship_id === f.friendship_id} onClick={() => {
                  setChatWith(f);
                  // Refresh list after mark-read runs so unread badge clears
                  setTimeout(fetchAll, 1500);
                }} />
                </div>
              ))
        )}

        {tab === 'requests' && !loading && (
          requests.length === 0
            ? <div style={{ padding: '3rem 1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                <UserCheck size={36} style={{ opacity: 0.2, marginBottom: 10, display: 'block', margin: '0 auto 10px' }} />
                No pending requests
              </div>
            : requests.map(r => (
                <RequestRow key={r.id} r={r}
                  onAccept={() => acceptRequest(r.requester_id)}
                  onDecline={() => declineRequest(r.requester_id)}
                />
              ))
        )}
      </div>
    </div>
  );

  const emptyChat = (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', gap: 12, padding: '2rem' }}>
      <MessageSquare size={48} style={{ opacity: 0.15 }} />
      <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>Select a friend to start chatting</div>
      <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>Text messages only · No files or media</div>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes typing-dot {
          0%, 60%, 100% { opacity: 0.2; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-3px); }
        }
      `}</style>

      {toast && <Toast message={toast.msg} type={toast.type} onDismiss={() => setToast(null)} />}

      {/* Page header — hide when chatting on mobile */}
      {!(isMobile && chatWith) && (
        <div className="page-header" style={{ marginBottom: '0.75rem' }}>
          <h1 className="page-title">Friends</h1>
          <p className="page-subtitle">Connect and chat with friends</p>
        </div>
      )}

      <div style={{
        display: 'flex', height: panelHeight, width: '100%',
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', overflow: 'hidden',
      }}>
        {/* Sidebar — hidden on mobile when chat is open */}
        {(!isMobile || !chatWith) && sidebar}

        {/* Chat panel */}
        {!isMobile && (
          <div style={{ flex: 1, minWidth: 0, display: 'flex', height: '100%', overflow: 'hidden' }}>
            {chatWith
              ? <ChatWindow friend={chatWith} currentUser={user} onBack={() => setChatWith(null)} isMobile={false} />
              : emptyChat
            }
          </div>
        )}

        {/* Mobile: full-screen chat */}
        {isMobile && chatWith && (
          <div style={{ width: '100%', height: '100%' }}>
            <ChatWindow friend={chatWith} currentUser={user} onBack={() => { setChatWith(null); fetchAll(); }} isMobile={true} />
          </div>
        )}
      </div>
    </>
  );
}
